import {
  IBrand,
  ITemplateDefinition,
  SystemTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  doc,
  type DocumentReference,
  type Transaction,
  type WithRef,
} from '@principle-theorem/shared';
import {
  SYSTEM_TEMPLATES_IDS,
  SystemTemplates,
} from '../templating/system-templates';
import { Brand } from './brand';

/**
 * Manages the adoption of system-defined template documents into a brand.
 * Centralises the "ensure doc exists, un-delete if soft-deleted" logic so
 * AutomationTemplate and future callers do not need to duplicate it.
 */
export class SystemTemplateAdoption {
  /**
   * Builds the deterministic brand-owned document reference for a repository
   * system template, so callers can read it directly inside transactions.
   */
  static docRef(
    systemTemplate: SystemTemplate,
    brand: WithRef<IBrand>
  ): DocumentReference<ITemplateDefinition> {
    return doc(Brand.templateCol(brand), SYSTEM_TEMPLATES_IDS[systemTemplate]);
  }

  /**
   * Applies the create-or-undelete adoption rule after the caller has already
   * read the deterministic document. It never reads, which keeps transaction
   * callers in control of Firestore's read-before-write ordering.
   */
  static async write(
    systemTemplate: SystemTemplate,
    brand: WithRef<IBrand>,
    existing?: WithRef<ITemplateDefinition>,
    transaction?: Transaction
  ): Promise<void> {
    if (existing && !existing.deleted) {
      return;
    }

    if (existing?.deleted) {
      await Firestore.patchDoc(existing.ref, { deleted: false }, transaction);
      return;
    }

    const [systemTemplateWithUid] = SystemTemplates.getSystemTemplatesWithUid(
      [systemTemplate],
      brand.ref
    );
    if (!systemTemplateWithUid) {
      throw new Error(`System template not found for: ${systemTemplate}`);
    }

    await Firestore.addDoc(
      Brand.templateCol(brand),
      systemTemplateWithUid.template,
      systemTemplateWithUid.uid,
      transaction
    );
  }

  /**
   * Ensures the system template document exists for the brand. Returns the
   * document ref, creating or un-deleting the document as needed.
   *
   * - If the document exists and is not deleted: returns its ref.
   * - If the document exists but is soft-deleted: clears the deleted flag and
   *   returns the same ref (prevents a duplicate at the deterministic id).
   * - If the document is absent: creates it from the repository default and
   *   returns the new ref.
   */
  static async ensureSystemTemplate(
    systemTemplate: SystemTemplate,
    brand: WithRef<IBrand>
  ): Promise<DocumentReference<ITemplateDefinition>> {
    const templateRef = SystemTemplateAdoption.docRef(systemTemplate, brand);
    const existingTemplate = await Firestore.safeGetDoc(templateRef);
    await SystemTemplateAdoption.write(systemTemplate, brand, existingTemplate);
    return templateRef;
  }
}
